// Global Instructions Rule Applied!
//
// Prospect Relationship tab (owner, 2026-09-03): who is on this process and
// what we know about the relationship — the prospect's own team (introduced
// by / sourced by / lead) and the prospect-scoped relationship facts. The
// account form's Relationship tab is the sibling; the Dashboard keeps the
// process picture (states, signals, drafts, brief) and the record. Facts hand
// their refetch up so the form's Refresh drives them (design patterns: data
// freshness); the team reads the record the form already refreshes.
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { ProspectTeam } from '../parts/ProspectTeam.tsx'
import { RelationshipFactsSection } from '../../../../relationships/components/RelationshipFactsSection.tsx'
import { selectProspectById } from '../../../../../state_manager/state_update_actions/fundraising/selectors.ts'
import { ProspectFields } from '../../../utils/fieldConfigs.ts'
import {
  deriveProspectContacts,
  EMPTY_PROSPECT_CONTACTS,
  ProspectContactsByRole
} from '../../../utils/prospectContacts.ts'

interface RelationshipTabProps {
  prospectRecord?: any
  recordId?: string | null
  onRefreshReady?: (refreshFn: () => Promise<void>) => void
}

export function RelationshipTab({ prospectRecord, recordId, onRefreshReady }: RelationshipTabProps) {
  const currentProspect = useSelector((state: any) =>
    recordId && recordId !== 'new' ? selectProspectById(state, recordId) : null
  )
  const prospect = prospectRecord || currentProspect
  const investorId = prospect?.[ProspectFields.prospects__investor] || null

  const [selectedContacts, setSelectedContacts] = useState<ProspectContactsByRole>(EMPTY_PROSPECT_CONTACTS)
  useEffect(() => {
    if (prospect) setSelectedContacts(deriveProspectContacts(prospect))
  }, [prospect])

  const factsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshAll = useCallback(async () => {
    await factsRef.current?.()
  }, [])
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(refreshAll)
  }, [onRefreshReady, refreshAll])

  return (
    <div
      data-debug='prospect-relationship-tab'
      data-component='RelationshipTab'
      className='h-full overflow-auto'
    >
      <div className='flex flex-col gap-2'>
        <ProspectTeam
          prospectId={recordId}
          recordId={recordId}
          formData={prospect}
          selectedContacts={selectedContacts}
          onContactsChange={setSelectedContacts}
          enableAutoPatch={!!recordId}
          includeInteractionDates={false}
        />
        {investorId && recordId ? (
          <RelationshipFactsSection
            accountId={investorId}
            prospectId={recordId}
            onRefreshReady={(fn) => {
              factsRef.current = fn
            }}
          />
        ) : null}
      </div>
    </div>
  )
}

export default RelationshipTab
